import { z } from 'zod';
import { checkStatusSchema } from './check.js';
import { onboardStateSchema, slugSchema } from './client.js';

/**
 * The headline numbers across the top of the overview.
 *
 * @since 1.0.0
 */
export const overviewKpisSchema = z.object({
    leadsDelivered24h: z.number().int(),
    clientCount: z.number().int(),
    openCritical: z.number().int(),
    openWarning: z.number().int(),
    formsMonitored: z.number().int(),
    formsTotal: z.number().int(),
    checksRunning: z.number().int(),
    checksTotal: z.number().int(),
    mailEventsStored: z.number().int(),
    mailEventsSince: z.number().int().nullable(),
});

export type OverviewKpis = z.infer<typeof overviewKpisSchema>;

/**
 * The latest outcome of one check for one client, as shown in a status square.
 *
 * @since 1.0.0
 */
export const clientCheckStatusSchema = z.object({
    status: checkStatusSchema,
    summary: z.string(),
    lastRunAt: z.number().int().nullable(),
    qcRunId: z.string().nullable(),
});

export type ClientCheckStatus = z.infer<typeof clientCheckStatusSchema>;

export const overviewClientSchema = z.object({
    slug: slugSchema,
    name: z.string(),
    sendingDomain: z.string(),
    onboardState: onboardStateSchema,
    flow: z.array(z.number().int()).length(24),
    hasFailure: z.boolean(),
    delivered24h: z.number().int(),
    checks: z.record(z.string(), clientCheckStatusSchema),
});

export type OverviewClient = z.infer<typeof overviewClientSchema>;

/**
 * One live check column on the client status table.
 *
 * @since 1.0.0
 */
export const overviewCheckColumnSchema = z.object({
    slug: z.string(),
    name: z.string(),
});

export type OverviewCheckColumn = z.infer<typeof overviewCheckColumnSchema>;

export const overviewResponseSchema = z.object({
    kpis: overviewKpisSchema,
    checkColumns: z.array(overviewCheckColumnSchema),
    clients: z.array(overviewClientSchema),
    lastEventAt: z.number().int().nullable(),
});

export type OverviewResponse = z.infer<typeof overviewResponseSchema>;
